import React, { useEffect } from "react";
import { Link, NavLink, useLocation, useNavigationType } from "react-router-dom";
import { Sun, Sprout, Users, Rows3, Bell, Search, Shield, MessageCircle, Plus, User } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { Avatar } from "@/components/Common";

const TABS = [
  { to: "/", label: "Oggi", icon: Sun, id: "oggi" },
  { to: "/cresci", label: "Cresci", icon: Sprout, id: "cresci" },
  { to: "/cresciamo", label: "Cresciamo", icon: Users, id: "cresciamo" },
  { to: "/feed", label: "Feed", icon: Rows3, id: "feed" },
] as const;

function Badge({ n, id }: { n: number; id: string }) {
  if (!n) return null;
  return <span data-testid={`nav-unread-badge-${id}`} className="absolute -top-1 -right-1.5 rounded-full text-[10px] font-bold min-w-[17px] h-[17px] px-1 flex items-center justify-center bg-[hsl(var(--primary))] text-white ring-2 ring-[hsl(var(--background))]">{n > 99 ? "99+" : n}</span>;
}

function useCounts() {
  const loc = useLocation();
  const { data: unread } = useQuery({ queryKey: ["unread", loc.pathname], queryFn: () => api.get("/notifications/unread").then((r) => r.data), refetchInterval: 30000 });
  const { data: dm } = useQuery({ queryKey: ["dm-unread"], queryFn: () => api.get("/dm/unread").then((r) => r.data), refetchInterval: 15000 });
  return { attivita: (unread?.notifications || 0) + (unread?.knocks || 0), messaggi: (dm?.messages || 0) + (dm?.requests || 0) };
}

// New pages open at the top; back/forward keeps the browser's position. Also clears a scroll lock left by a sheet closed by navigation.
function useScrollReset() {
  const loc = useLocation();
  const type = useNavigationType();
  useEffect(() => {
    if (type !== "POP") window.scrollTo(0, 0);
    const t = setTimeout(() => {
      if (!document.querySelector("[role=dialog]")) {
        document.body.removeAttribute("data-scroll-locked");
        document.body.style.removeProperty("overflow");
        document.body.style.removeProperty("pointer-events");
      }
    }, 50);
    return () => clearTimeout(t);
  }, [loc.pathname, type]);
}

export const Logo = () => <Link to="/" className="font-display text-[1.15rem] font-extrabold tracking-[0.18em]" data-testid="logo-link">UNSEEN</Link>;

function TopIcon({ to, label, id, n = 0, children }: any) {
  return <NavLink to={to} aria-label={label} data-testid={`top-${id}-btn`} className={({ isActive }) => `icon-btn relative ${isActive ? "bg-white/60" : ""}`}><span className="relative">{children}<Badge n={n} id={id} /></span></NavLink>;
}

function MobileTopBar({ counts, user }: any) {
  return (
    <header className="md:hidden fixed top-0 inset-x-0 z-40 h-[var(--top-h)] pt-[env(safe-area-inset-top)] px-3 flex items-center justify-between bg-[hsl(162_17%_80%/.72)] border-b border-white/50 backdrop-blur-xl" data-testid="top-bar">
      <span className="pl-2"><Logo /></span>
      <div className="flex items-center">
        <TopIcon to="/cerca" label="Cerca" id="cerca"><Search size={20} /></TopIcon>
        <TopIcon to="/pubblica" label="Pubblica" id="pubblica"><Plus size={21} /></TopIcon>
        <TopIcon to="/messaggi" label="Messaggi" id="messaggi" n={counts.messaggi}><MessageCircle size={20} /></TopIcon>
        <TopIcon to="/attivita" label="Notifiche" id="attivita" n={counts.attivita}><Bell size={20} /></TopIcon>
        <NavLink to="/profilo" aria-label="Profilo" className="ml-1 w-11 h-11 flex items-center justify-center" data-testid="top-profile-btn"><Avatar user={user} size={30} /></NavLink>
      </div>
    </header>
  );
}

function SideNav({ counts, user }: any) {
  const side = ({ isActive }: any) => `relative flex items-center gap-3.5 mx-3 px-4 min-h-[44px] rounded-2xl ${isActive ? "nav-side-active font-semibold" : "text-ink/70 hover:text-foreground hover:bg-white/40"}`;
  const item = (to: string, label: string, Icon: any, id: string, n = 0, end = false) => (
    <NavLink key={to} to={to} end={end} className={side} data-testid={`side-nav-${id}`}><span className="relative"><Icon size={20} /><Badge n={n} id={`side-${id}`} /></span>{label}</NavLink>
  );
  return (
    <aside className="hidden md:flex fixed inset-y-3 left-3 w-60 flex-col glass rounded-[1.75rem] z-40 py-6">
      <div className="px-7 pb-7"><Logo /></div>
      <nav className="flex flex-col gap-1">
        {TABS.map((tb) => item(tb.to, tb.label, tb.icon, tb.id, 0, tb.to === "/"))}
        <div className="mx-7 my-3 border-t border-ink/10" />
        {item("/messaggi", "Messaggi", MessageCircle, "messaggi", counts.messaggi)}
        {item("/attivita", "Notifiche", Bell, "attivita", counts.attivita)}
        {item("/pubblica", "Pubblica", Plus, "pubblica")}
        {item("/cerca", "Cerca", Search, "cerca")}
        {item("/profilo", "Profilo", User, "profilo")}
        {user?.role === "admin" && item("/admin", "Admin", Shield, "admin")}
      </nav>
      <div className="mt-auto px-7 text-xs text-ink/55 space-x-3">
        <a href="/privacy" className="hover:text-foreground">Privacy</a><a href="/termini" className="hover:text-foreground">Termini</a>
      </div>
    </aside>
  );
}

function BottomNav() {
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 px-3 pb-[max(env(safe-area-inset-bottom),10px)]" data-testid="bottom-nav">
      <div className="h-[62px] grid grid-cols-4 rounded-[1.6rem] glass glass-strong">
        {TABS.map((tb) => (
          <NavLink key={tb.to} to={tb.to} end={tb.to === "/"} data-testid={`bottom-nav-${tb.id}`} aria-label={tb.label}
            className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 rounded-[1.3rem] m-1 ${isActive ? "bg-white/70 text-foreground shadow-[0_1px_3px_rgba(22,44,38,.1)]" : "text-ink/60"}`}>
            {({ isActive }: any) => (<><tb.icon size={21} strokeWidth={isActive ? 2.3 : 1.8} /><span className="text-[11px] font-semibold">{tb.label}</span></>)}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const loc = useLocation();
  const counts = useCounts();
  useScrollReset();
  const inChat = /^\/messaggi\/.+/.test(loc.pathname);
  return (
    <div className={`min-h-[100dvh] md:pl-[16.5rem] ${inChat ? "app-chat" : "app-shell"}`}>
      <SideNav counts={counts} user={user} />
      {!inChat && <MobileTopBar counts={counts} user={user} />}
      <main key={loc.pathname} className={inChat ? "" : "pt-[var(--top-h)] pb-[var(--nav-h)] md:pt-0 md:pb-0"}>{children}</main>
      {!inChat && <BottomNav />}
    </div>
  );
}
